import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import MainLayout from "@/components/feature/MainLayout";
import { commercialUsers } from "@/mocks/users";
import { safetyInspectionHistory } from "@/mocks/commercialDetail";
import FlowmeterPanel from "./components/FlowmeterPanel";
import MultiYearChart from "./components/MultiYearChart";

const tabs = [
  { key: "overview", label: "企业档案", icon: "ri-building-2-line" },
  { key: "flowmeter", label: "流量计数据", icon: "ri-dashboard-line" },
  { key: "history", label: "多年用气对比", icon: "ri-bar-chart-grouped-line" },
  { key: "safety", label: "安检记录", icon: "ri-shield-check-line" },
];

export default function CommercialDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("overview");

  const user = commercialUsers.find(u => u.id === id);

  if (!user) {
    return (
      <MainLayout>
        <div className="flex flex-col items-center justify-center h-64 text-gray-400">
          <div className="w-12 h-12 flex items-center justify-center mb-3">
            <i className="ri-error-warning-line text-4xl text-gray-200"></i>
          </div>
          <p className="text-sm">未找到该用户信息</p>
          <button onClick={() => navigate("/profile/commercial")} className="mt-3 text-xs text-[#E8531A] cursor-pointer">返回列表</button>
        </div>
      </MainLayout>
    );
  }

  const typeColorMap: Record<string, string> = {
    "小餐饮业": "bg-amber-50 text-amber-600",
    "特殊行业": "bg-violet-50 text-violet-600",
    "大型企事业": "bg-blue-50 text-blue-700",
  };

  return (
    <MainLayout>
      <div className="space-y-4">
        {/* Back */}
        <button onClick={() => navigate("/profile/commercial")} className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-gray-600 cursor-pointer">
          <i className="ri-arrow-left-line"></i>返回工商业用户列表
        </button>

        {/* Header Banner */}
        <div className="rounded-2xl overflow-hidden relative" style={{ background: "linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)" }}>
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-4 right-8 w-32 h-32 rounded-full border border-white/20"></div>
            <div className="absolute top-8 right-16 w-20 h-20 rounded-full border border-white/10"></div>
          </div>
          <div className="relative p-6 flex items-start justify-between gap-4">
            <div className="flex items-start gap-4">
              <div className="w-16 h-16 rounded-2xl bg-white/10 flex items-center justify-center flex-shrink-0">
                <i className="ri-building-2-line text-white text-3xl"></i>
              </div>
              <div>
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <h1 className="text-xl font-bold text-white">{user.name}</h1>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${typeColorMap[user.type] || "bg-gray-100 text-gray-600"}`}>{user.type}</span>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${user.status === "正常" ? "bg-green-500/20 text-green-300" : "bg-red-500/20 text-red-300"}`}>{user.status}</span>
                </div>
                <p className="text-sm text-white/60 mb-1">{user.address}</p>
                <p className="text-xs text-white/40">统一社会信用代码：{user.license} · 联系人：{user.contact} · {user.phone}</p>
                <p className="text-xs text-white/40 mt-0.5">开户日期：{user.accountDate}</p>
              </div>
            </div>
            <div className="flex items-center gap-6 flex-shrink-0">
              <div className="text-center">
                <p className="text-xs text-white/40 mb-1">账户余额</p>
                <p className="text-2xl font-bold text-[#E8531A]">¥{user.balance.toLocaleString()}</p>
              </div>
              <div className="text-center">
                <p className="text-xs text-white/40 mb-1">本月用气</p>
                <p className="text-2xl font-bold text-white">{user.monthlyUsage.toLocaleString()}</p>
                <p className="text-xs text-white/40">m³</p>
              </div>
              <div className="text-center">
                <p className="text-xs text-white/40 mb-1">安全评级</p>
                <p className="text-2xl font-bold text-emerald-400">{user.safetyGrade}</p>
              </div>
            </div>
          </div>

          {/* Quick Stats */}
          <div className="border-t border-white/10 px-6 py-3 flex items-center gap-8">
            <div className="flex items-center gap-2 text-xs text-white/50">
              <i className="ri-cpu-line"></i>
              <span>设备 {user.devices.length} 台</span>
            </div>
            <div className="flex items-center gap-2 text-xs text-white/50">
              <i className="ri-shield-check-line"></i>
              <span>上次安检：{user.lastInspection}</span>
            </div>
            <div className="flex items-center gap-2 text-xs text-white/50">
              <i className="ri-calendar-line"></i>
              <span>下次安检：{user.nextInspection}</span>
            </div>
            <div className={`flex items-center gap-2 text-xs ${user.insurance.valid ? "text-green-400" : "text-red-400"}`}>
              <i className="ri-shield-line"></i>
              <span>保险{user.insurance.valid ? `有效至 ${user.insurance.expiry}` : "已过期"} · {user.insurance.company}</span>
            </div>
            <div className="flex items-center gap-2 text-xs text-white/50">
              <i className="ri-bar-chart-line"></i>
              <span>安全评分：{user.safetyLevel}分</span>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-1 bg-gray-100 rounded-xl p-1 w-fit">
          {tabs.map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-1.5 px-4 py-2 text-xs rounded-lg transition-colors cursor-pointer whitespace-nowrap ${
                activeTab === tab.key ? "bg-white text-gray-900 font-semibold" : "text-gray-500 hover:text-gray-700"
              }`}
            >
              <i className={tab.icon}></i>
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        {activeTab === "overview" && (
          <div className="grid grid-cols-12 gap-4">
            {/* Basic Info */}
            <div className="col-span-8 space-y-4">
              <div className="bg-white rounded-xl border border-gray-100 p-5">
                <h3 className="text-sm font-bold text-gray-900 mb-4">企业基本信息</h3>
                <div className="grid grid-cols-2 gap-x-8 gap-y-3">
                  {[
                    { label: "企业名称", value: user.name },
                    { label: "用户类型", value: user.type },
                    { label: "统一社会信用代码", value: user.license },
                    { label: "联系人", value: user.contact },
                    { label: "联系电话", value: user.phone },
                    { label: "开户日期", value: user.accountDate },
                    { label: "企业地址", value: user.address },
                    { label: "账户状态", value: user.status },
                  ].map(item => (
                    <div key={item.label} className="flex items-start gap-2">
                      <span className="text-xs text-gray-400 w-28 flex-shrink-0">{item.label}</span>
                      <span className="text-xs font-medium text-gray-800">{item.value}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Devices */}
              <div className="bg-white rounded-xl border border-gray-100 p-5">
                <h3 className="text-sm font-bold text-gray-900 mb-4">计量设备信息</h3>
                <div className="space-y-3">
                  {user.devices.map(dev => (
                    <div key={dev.id} className="p-4 bg-gray-50 rounded-xl">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 flex items-center justify-center bg-orange-100 rounded-lg">
                            <i className={`${dev.type === "流量计" ? "ri-dashboard-line" : dev.type === "压力变送器" ? "ri-pulse-line" : "ri-alarm-warning-line"} text-[#E8531A] text-sm`}></i>
                          </div>
                          <div>
                            <p className="text-xs font-bold text-gray-800">{dev.type} · {dev.model}</p>
                            <p className="text-xs text-gray-400">IoT编号：{dev.iotId}</p>
                          </div>
                        </div>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${dev.status === "正常" ? "bg-green-50 text-green-600" : "bg-amber-50 text-amber-600"}`}>
                          {dev.status}
                        </span>
                      </div>
                      <div className="grid grid-cols-3 gap-x-4 gap-y-1.5">
                        {[
                          { label: "规格型号", value: dev.spec },
                          { label: "生产厂家", value: dev.manufacturer },
                          { label: "出厂日期", value: dev.productionDate },
                          { label: "安装日期", value: dev.installDate },
                          { label: "上次校验", value: dev.calibrationDate },
                          { label: "下次校验", value: dev.nextCalibration },
                        ].map(item => (
                          <div key={item.label}>
                            <span className="text-xs text-gray-400">{item.label}：</span>
                            <span className="text-xs font-medium text-gray-700">{item.value}</span>
                          </div>
                        ))}
                      </div>
                      {dev.currentReading !== null && (
                        <div className="mt-2 pt-2 border-t border-gray-200 flex items-center gap-4">
                          <span className="text-xs text-gray-400">当前读数：</span>
                          <span className="text-sm font-bold text-[#E8531A]">{dev.currentReading.toLocaleString()}</span>
                          {"pressure" in dev && <span className="text-xs text-gray-500">压力：{(dev as { pressure: string }).pressure}</span>}
                          {"temperature" in dev && <span className="text-xs text-gray-500">温度：{(dev as { temperature: string }).temperature}</span>}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Right Panel */}
            <div className="col-span-4 space-y-4">
              {/* Safety Score */}
              <div className="bg-white rounded-xl border border-gray-100 p-5">
                <h3 className="text-sm font-bold text-gray-900 mb-4">安全评分</h3>
                <div className="flex flex-col items-center mb-4">
                  <div className="relative w-28 h-28">
                    <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
                      <circle cx="50" cy="50" r="40" fill="none" stroke="#f1f5f9" strokeWidth="10" />
                      <circle
                        cx="50" cy="50" r="40" fill="none"
                        stroke={user.safetyLevel >= 90 ? "#10b981" : user.safetyLevel >= 70 ? "#f59e0b" : "#ef4444"}
                        strokeWidth="10"
                        strokeDasharray={`${(user.safetyLevel / 100) * 251.2} 251.2`}
                        strokeLinecap="round"
                      />
                    </svg>
                    <div className="absolute inset-0 flex flex-col items-center justify-center">
                      <span className="text-2xl font-bold text-gray-900">{user.safetyLevel}</span>
                      <span className="text-xs text-gray-400">分</span>
                    </div>
                  </div>
                  <span className={`mt-2 text-sm font-bold ${user.safetyLevel >= 90 ? "text-emerald-600" : "text-amber-600"}`}>
                    {user.safetyGrade} 级
                  </span>
                </div>
                {[
                  { label: "设备状态", score: 95 },
                  { label: "安检合规", score: user.safetyLevel },
                  { label: "保险覆盖", score: user.insurance.valid ? 100 : 0 },
                  { label: "用气规范", score: 88 },
                  { label: "报警响应", score: 92 },
                ].map(item => (
                  <div key={item.label} className="mb-2">
                    <div className="flex justify-between text-xs mb-1">
                      <span className="text-gray-500">{item.label}</span>
                      <span className="font-medium text-gray-700">{item.score}</span>
                    </div>
                    <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full"
                        style={{
                          width: `${item.score}%`,
                          backgroundColor: item.score >= 90 ? "#10b981" : item.score >= 70 ? "#f59e0b" : "#ef4444",
                        }}
                      ></div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Insurance */}
              <div className="bg-white rounded-xl border border-gray-100 p-5">
                <h3 className="text-sm font-bold text-gray-900 mb-3">保险信息</h3>
                <div className={`p-3 rounded-lg ${user.insurance.valid ? "bg-green-50" : "bg-red-50"}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-6 h-6 flex items-center justify-center">
                      <i className={`ri-shield-line text-lg ${user.insurance.valid ? "text-green-600" : "text-red-500"}`}></i>
                    </div>
                    <span className={`text-xs font-semibold ${user.insurance.valid ? "text-green-700" : "text-red-600"}`}>
                      {user.insurance.valid ? "保险有效" : "保险已过期"}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500">承保公司：{user.insurance.company}</p>
                  <p className="text-xs text-gray-500">有效期至：{user.insurance.expiry}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === "flowmeter" && <FlowmeterPanel userId={user.id} />}

        {activeTab === "history" && <MultiYearChart userId={user.id} />}

        {activeTab === "safety" && (
          <div className="bg-white rounded-xl border border-gray-100 p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-bold text-gray-900">安检历史记录</h3>
              <button className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-[#E8531A] text-white rounded-lg hover:bg-[#d44a15] cursor-pointer whitespace-nowrap">
                <i className="ri-add-line"></i>新增安检
              </button>
            </div>
            <div className="space-y-3">
              {safetyInspectionHistory.map((record, idx) => (
                <div key={idx} className="flex items-start gap-4 p-4 bg-gray-50 rounded-xl">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${record.result === "合格" ? "bg-green-100" : "bg-red-100"}`}>
                    <i className={`${record.result === "合格" ? "ri-shield-check-line text-green-600" : "ri-shield-cross-line text-red-500"} text-lg`}></i>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold text-gray-800">{record.date}</span>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${record.result === "合格" ? "bg-green-50 text-green-600" : "bg-red-50 text-red-600"}`}>
                          {record.result}
                        </span>
                        <span className="text-xs text-gray-400">评分：<span className="font-semibold text-gray-700">{record.score}分</span></span>
                      </div>
                      <span className="text-xs text-gray-400">安检员：{record.inspector}</span>
                    </div>
                    <p className="text-xs text-gray-500">问题记录：{record.issues}</p>
                    <p className="text-xs text-gray-400 mt-0.5">下次安检：{record.nextDate}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </MainLayout>
  );
}
